import { inject, Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { concat, Observable, of, OperatorFunction, switchMap, tap } from 'rxjs';
import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  SURVEY_CLASSIFICATION_KIND,
  SURVEY_MESSAGES,
  savedTargetingLosses,
  Survey,
  SurveyKind,
  TEMPLATE_MESSAGES,
  targetingLossMessages,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveyCreationCoreApiActions } from '../../survey-creation-core.actions';
import {
  SurveyPersistenceService,
  SurveyUpdateRequest,
} from '../../../../services/save/survey-persistence.service';
import { traditionalMcLocaleMessage } from '../../utils/survey-save-pipeline.helper';
import {
  SurveySaveBuild,
  SurveySaveFollowUp,
} from '../survey-save.pipeline.types';

// Update stage: writes, skips or rejects as built, then runs the follow-up.
@Injectable({ providedIn: 'root' })
export class SurveySaveUpdateStage extends EffectsBase {
  private readonly _toastService = inject(ToastService);
  private readonly _persistence = inject(SurveyPersistenceService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);

  public persist(
    followUp: SurveySaveFollowUp,
  ): OperatorFunction<SurveySaveBuild, Action> {
    return (build$) =>
      build$.pipe(switchMap((build) => this._persistBuild(build, followUp)));
  }

  private _persistBuild(
    build: SurveySaveBuild,
    followUp: SurveySaveFollowUp,
  ): Observable<Action> {
    return match(build)
      .with({ kind: 'write' }, ({ request, survey, silent }) =>
        this._writeSurveyUpdate(request, followUp, silent, survey),
      )
      .with({ kind: 'reject' }, ({ error }) =>
        this._rejectUnbuildableSave(error),
      )
      .exhaustive();
  }

  private _rejectUnbuildableSave(error: string): Observable<Action> {
    this._toastService.error(error, { keepOpen: false });
    return of(SurveyCreationCoreApiActions.saveSurveyFailure({ error }));
  }

  private _writeSurveyUpdate(
    request: SurveyUpdateRequest,
    followUp: SurveySaveFollowUp,
    silent: boolean,
    survey: Nullable<Survey>,
  ): Observable<Action> {
    return this._persistence.update(request).pipe(
      tap((savedSurvey) => {
        if (!silent) {
          this._toastService.success(this._savedMessage());
        }
        this._toastTargetingLosses(request, savedSurvey);
      }),
      switchMap((savedSurvey) =>
        concat(
          of(
            SurveyCreationCoreApiActions.saveSurveySuccess({
              survey: savedSurvey,
            }),
          ),
          followUp(savedSurvey, silent),
        ),
      ),
      this.psCatchError(
        (error) => {
          const message = this._saveErrorMessage(error.message, survey);
          this._toastService.error(message);
          return of(
            SurveyCreationCoreApiActions.saveSurveyFailure({
              error: message,
            }),
          );
        },
        { notify: false },
      ),
    );
  }

  private _savedMessage(): string {
    return this._authSelectors.isMasterMultiBuyer()
      ? TEMPLATE_MESSAGES.SAVED
      : SURVEY_MESSAGES.SAVED;
  }

  // Only a standard write returns the row it was sent, so only it can be diffed.
  private _toastTargetingLosses(
    request: SurveyUpdateRequest,
    savedSurvey: Survey,
  ): void {
    if (request.kind !== SURVEY_CLASSIFICATION_KIND.STANDARD) {
      return;
    }
    if (!savedSurvey.qualifications) {
      return;
    }
    const losses = savedTargetingLosses(request.input.targeting, {
      qualifications: savedSurvey.qualifications,
      quotas: savedSurvey.quotas ?? [],
    });
    for (const message of targetingLossMessages(losses)) {
      this._toastService.warning(message, { keepOpen: false });
    }
  }

  private _saveErrorMessage(message: string, survey: Nullable<Survey>): string {
    return Option.from(survey)
      .filter((located) => SurveyKind.isTraditionalMc(located))
      .mapOr(message, (located) =>
        traditionalMcLocaleMessage(message, located),
      );
  }
}
